import { AxiosResponse } from 'axios';
import { SagaIterator } from 'redux-saga';
import {
  call,
  fork,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { DataKi, DataKiData, DataKiParams } from '../domain/dataKi';
import {
  extractMainTableKiCodes,
  hasExcludedKiTagAverageTarget,
  hasTaggedKiMatchForTag,
  hasTaggedKiMatchInMainTable,
} from '../domain/ki/calcExcludedKiTagKiCount';
import {
  applyProperNyushoSRequestFields,
  insertMissingProperNyushoSColumnsOrder,
  retryProperNyushoSRequest,
} from '../domain/ki/properNyushoS';
import {
  Column,
  KiTagListOptions,
  LoadingState,
  SearchCondition,
} from '../domain/schemas';
import { SettingsOptionsKi } from '../domain/settingsOptionsKi';
import { ShuOption } from '../domain/shu';

import {
  ChangeDataKiColumnsOrderAction,
  DataKiActionCreators,
  DataKiActionTypes,
  FetchDataKiAction,
  FetchDataKiExcludedKiTagAverageRequestAction,
  FetchDataKiOnlyOneKiTagAverageRequestAction,
  dataKiColumnsOrderSelector,
  dataKiDataColumnsSelector,
  dataKiFilteredKiTagAverageTagsValueSelector,
  dataKiOnlyOneKiTagAverageSelector,
  dataKiOrderedDataSelector,
  dataKiParamsSelector,
  dataKiSelectedKiListSelector,
} from '../redux/server/dataKi';
import {
  SettingsOptionsKiActionTypes,
  settingsOptionsKiLoadingSelector,
  settingsOptionsKiSearchConditionSelector,
  settingsOptionsKiSelector,
} from '../redux/server/settingsOptionsKi';
import {
  modelReportsSelectedCurrentShuSelector,
  modelReportsSelectedShuSelector,
  selectModelReportsCurrentShuAction,
  selectModelReportsShuAction,
  selectModelReportsTableHeaderSubMenuMultiFilterSelectOptionsAction,
} from '../redux/ui/modelReportsSetting';
import { Api, buildConfig } from '../utils/api';
import { recalcColumnOrder } from '../utils/orderedCell';
import {
  convertShuOption,
  existShuOption,
  removeDuplicateShuOptions,
  selectShu2SearchCondition,
} from '../utils/shu';
import {
  MODEL_REPORTS_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER,
  ORDER_LIST_FOR_PPM,
} from '../utils/tableHeaderSubMenuMultiFilter';
import { validateQuery } from '../utils/validateQuery';
import { handleErrorSaga } from './errorSaga';

function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return String(value);
  if (typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
    .join(',')}}`;
}

// dataKiのデータを取得する
export function* fetchDataKiSaga(api: Api, action: FetchDataKiAction) {
  try {
    yield put(DataKiActionCreators.resetDataKiOnlyOneKiTagAverageAction());
    yield put(DataKiActionCreators.fetchDataKiRequestAction());
    const params: DataKiParams = {
      ...action.payload.params, // 変更後の検索条件
      ...(action.payload.currentShu
        ? selectShu2SearchCondition(action.payload.currentShu)
        : {}), // 変更後の種別・種別グループ
      halls: action.payload.currentHalls, //変更後の店舗
    };

    validateQuery(params);

    const previousSetting: DataKiParams | undefined = yield select(
      dataKiParamsSelector
    );
    const firstRequestParams = applyProperNyushoSRequestFields(
      params,
      previousSetting
    );
    const firstResponse: AxiosResponse<DataKi> = yield call(
      api.get,
      '/data/ki',
      buildConfig(firstRequestParams)
    );
    const retriedParams = retryProperNyushoSRequest(
      firstRequestParams,
      firstResponse.data.setting.fields
    );
    const requestParams = retriedParams ?? firstRequestParams;
    const response: AxiosResponse<DataKi> =
      retriedParams === undefined
        ? firstResponse
        : yield call(api.get, '/data/ki', buildConfig(requestParams));
    const dataKi = response.data;

    yield put(DataKiActionCreators.fetchDataKiSuccessAction(dataKi));

    // マルチフィルターを表示するカラムの列インデックスを求める
    const columnIndexMap =
      MODEL_REPORTS_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER.reduce(
        (acc, code) => {
          const idx = dataKi.data.columns.findIndex((c) => c.code === code);
          if (idx >= 0) {
            acc[code] = idx;
          }
          return acc;
        },
        {} as Record<string, number>
      );

    // 表示項目「type」用の理想配列順を取得
    const settingsOptions: SearchCondition = yield select(
      settingsOptionsKiSearchConditionSelector
    );
    const sisTypeNames: string[] = settingsOptions.sisTypes.map(
      (item) => item.name
    );

    const processColumnValues = (code: string, values: string[]): string[] => {
      switch (code) {
        case 'ppmKbn':
        case 'sisPpmKbn':
        case 'ppmKbnComparison':
          return ORDER_LIST_FOR_PPM.filter((label) => values.includes(label));
        case 'type':
          return sisTypeNames.filter(
            (name) => values.includes(name) && name !== '-'
          );

        default:
          return values;
      }
    };

    // マルチセレクトフィルタ用の選択肢オブジェクトをカラムごとに作成
    const newOptions =
      MODEL_REPORTS_COLUMNS_LIST_FOR_TABLE_HEADER_SUB_MENU_MULTI_FILTER.reduce<
        Record<string, string[]>
      >((acc, code) => {
        const index = columnIndexMap[code];
        if (index === undefined) {
          acc[code] = [];
          return acc;
        }

        const values = dataKi.data.rows
          .map((row) => row.data.at(index)?.value)
          .filter((v): v is string => typeof v === 'string');

        const uniqueValues = Array.from(new Set(values));
        acc[code] = processColumnValues(code, uniqueValues);

        return acc;
      }, {});

    yield put(
      selectModelReportsTableHeaderSubMenuMultiFilterSelectOptionsAction(
        newOptions
      )
    );

    // チェックした機種で絞り込みしているとき、
    // 絞り込んだ機種のみの平均合計行を取得する
    const modelsFilteredByChecks: string[] = yield select(
      dataKiSelectedKiListSelector
    );
    if (modelsFilteredByChecks.length > 0) {
      yield put(
        DataKiActionCreators.fetchDataKiFilteredAverageRequestAction(
          requestParams,
          modelsFilteredByChecks
        )
      );
    }

    // 選択された機種タグを抽出
    const fetchedSelectedKiTags: KiTagListOptions[] = yield select(
      dataKiFilteredKiTagAverageTagsValueSelector
    );

    const mainTableKiCodes = extractMainTableKiCodes(dataKi.data.rows);

    // 機種タグ絞り込みで機種行が、DKSISやマーキング、表示項目などで変更されたとき
    // 機種タグに紐づいた機種の平均合計行を取得する
    // 全体のタグ絞り込み平均合計は「タグが2つ以上選択されている場合のみ」取得する
    if (fetchedSelectedKiTags.length > 1) {
      // 詳細条件の機種とタグの積集合が 0 件なら API を送らない（タグ選択状態は維持）
      if (
        hasTaggedKiMatchInMainTable(mainTableKiCodes, fetchedSelectedKiTags)
      ) {
        yield put(
          DataKiActionCreators.fetchDataKiFilteredKiTagAverageRequestAction(
            requestParams,
            fetchedSelectedKiTags
          )
        );
      } else {
        // 該当機種0件のときはリクエストせず、選択状態のみ維持する
        yield put(DataKiActionCreators.resetDataKiFilteredKiTagAverageAction());
        yield put(
          DataKiActionCreators.selectDataKiFilteredKiTagAverageAction(
            fetchedSelectedKiTags
          )
        );
      }
    }
    // 個別タグの平均合計はタグが1つ以上選択されていれば取得する
    if (fetchedSelectedKiTags.length > 0) {
      const matchingTags = fetchedSelectedKiTags.filter((tag) =>
        hasTaggedKiMatchForTag(mainTableKiCodes, tag)
      );
      // 積集合 0 件タグの古い個別平均行を除去してから fetch する
      yield put(
        DataKiActionCreators.removeDataKiOnlyOneKiTagAverageAction(matchingTags)
      );
      for (let i = 0; i < matchingTags.length; i++) {
        const tag = matchingTags.at(i);
        if (tag?.tagId != null) {
          yield put(
            DataKiActionCreators.fetchDataKiOnlyOneKiTagAverageRequestAction(
              requestParams,
              tag.tagId.toString()
            )
          );
        }
      }
      if (
        hasTaggedKiMatchInMainTable(mainTableKiCodes, fetchedSelectedKiTags) &&
        hasExcludedKiTagAverageTarget(mainTableKiCodes, fetchedSelectedKiTags)
      ) {
        yield put(
          DataKiActionCreators.fetchDataKiExcludedKiTagAverageRequestAction(
            requestParams,
            fetchedSelectedKiTags
          )
        );
      } else {
        yield put(DataKiActionCreators.resetDataKiExcludedKiTagAverageAction());
      }
    } else {
      // タグ未選択時は対象外平均行が残って誤表示されないように必ずリセットする
      yield put(DataKiActionCreators.resetDataKiExcludedKiTagAverageAction());
    }
  } catch (error: unknown) {
    yield put(DataKiActionCreators.renewDataKi());
    yield fork(handleErrorSaga, error);
  }
}

// チェックされた機種のみの平均合計行データを取得する
function* fetchDataKiFilteredAverageSaga(api: Api, action: FetchDataKiAction) {
  try {
    const params = {
      ...action.payload.params,
      containsData: false,
    };

    validateQuery(params);

    const response: AxiosResponse<DataKi> = yield call(
      api.get,
      '/data/ki',
      buildConfig(params)
    );
    const dataKi = response.data;

    // 機種タグ設定 機種タグ条件のリセット処理
    yield put(DataKiActionCreators.resetDataKiFilteredKiTagAverageAction());

    // タグ別平均行のリセット処理
    yield put(DataKiActionCreators.resetDataKiOnlyOneKiTagAverageAction());

    yield put(DataKiActionCreators.resetDataKiExcludedKiTagAverageAction());

    yield put(
      DataKiActionCreators.fetchDataKiFilteredAverageSuccessAction(dataKi)
    );
  } catch (error: unknown) {
    yield put(DataKiActionCreators.renewDataKi());
    yield fork(handleErrorSaga, error);
  }
}

// 機種タグを選択した機種のみの平均合計行データを取得する（タグ絞り込み 平均/合計）
// 積集合 0 件のガードは dispatch 前（Popover / fetchDataKiSaga）で行う
function* fetchDataKiFilteredKiTagAverageSaga(
  api: Api,
  action: FetchDataKiAction
) {
  try {
    const params = {
      ...action.payload.params,
      containsData: false,
    };

    validateQuery(params);

    const response: AxiosResponse<DataKi> = yield call(
      api.get,
      '/data/ki',
      buildConfig(params)
    );
    const dataKi = response.data;

    yield put(
      DataKiActionCreators.fetchDataKiFilteredKiTagAverageSuccessAction(dataKi)
    );
  } catch (error: unknown) {
    yield put(DataKiActionCreators.renewDataKi());
    yield fork(handleErrorSaga, error);
  }
}

// 指定タグ以外の機種のみの平均合計行データを取得する
export function* fetchDataKiExcludedKiTagAverageSaga(
  api: Api,
  action: FetchDataKiExcludedKiTagAverageRequestAction
) {
  try {
    const params = {
      ...action.payload.params,
      containsData: false,
    };

    validateQuery(params);

    const response: AxiosResponse<DataKi> = yield call(
      api.get,
      '/data/ki',
      buildConfig(params)
    );
    const dataKi = response.data;

    yield put(
      DataKiActionCreators.fetchDataKiExcludedKiTagAverageSuccessAction(dataKi)
    );
  } catch (error: unknown) {
    yield put(DataKiActionCreators.resetDataKiExcludedKiTagAverageAction());
    yield fork(handleErrorSaga, error);
  }
}
// in-flightは tagId + serialized params の複合キーで管理する
// 積集合 0 件のガードは dispatch 前（Popover / fetchDataKiSaga）で行う
function* fetchDataKiOnlyOneKiTagAverageSaga(
  api: Api,
  inFlightOnlyOneTagKeys: Set<string>,
  action: FetchDataKiOnlyOneKiTagAverageRequestAction
): SagaIterator {
  const tagId = action.payload.params.tagIds?.[0];
  // tagId は number/string が混在し得るため、比較用に正規化して扱う
  const normalizedTagId = tagId != null ? String(tagId) : undefined;
  // 検索パラメータを安定化して一致判定に使うため事前に作成
  const params = {
    ...action.payload.params,
    containsData: false,
  } as DataKiParams;

  // 合成キー（tagId + 検索パラメータの安定化した文字列表現）
  let paramKey: string | undefined = undefined;
  // この saga が inFlight に追加したかどうかのフラグ（他の並列 saga と競合しないため）
  let addedToInFlight = false;

  try {
    // tagIdが存在しない場合はリクエストしない
    if (!normalizedTagId) return;

    // validate を先に行って不正なパラメータを弾く
    validateQuery(params);

    // 重複判定に使用する主要フィールドだけでキーを生成する（レスポンスとリクエストで余分なフィールド差があるため）
    const makeRequestKey = (p: Partial<DataKiParams>) =>
      stableStringify({
        // tagIds は number/string 混在し得るので、常に string[] として安定化する
        tagIds: Array.isArray(p.tagIds)
          ? p.tagIds.map((id) => String(id))
          : null,
        mainField: p.mainField ?? null,
        fields: p.fields ?? null,
        marking: p.marking ?? null,
        kiList: p.kiList ?? null,
        startDate: p.startDate ?? null,
        endDate: p.endDate ?? null,
        startComparisonDate: p.startComparisonDate ?? null,
        endComparisonDate: p.endComparisonDate ?? null,
        ymdList: p.ymdList ?? null,
        sisTypes: p.sisTypes ?? null,
        makers: p.makers ?? null,
        halls: p.halls ?? null,
        sort: p.sort ?? null,
        order: p.order ?? null,
      });

    const requestKey = makeRequestKey(params);
    paramKey = `${normalizedTagId}|${requestKey}`;

    // 既に同一 tagId & 同一 params のデータが存在する場合はリクエストしない
    const existingState = yield select(dataKiOnlyOneKiTagAverageSelector);
    const existingData = existingState?.data ?? [];
    if (
      existingData &&
      existingData.some((item: DataKi) => {
        const existingTagId = item.setting.tagIds?.[0];
        if (existingTagId == null) return false;
        if (String(existingTagId) !== normalizedTagId) return false;
        const existingKey = makeRequestKey(item.setting);
        return existingKey === requestKey;
      })
    ) {
      return;
    }

    // in-flight も合成キーで管理して重複リクエストを防ぐ
    if (inFlightOnlyOneTagKeys.has(paramKey)) {
      return;
    }
    inFlightOnlyOneTagKeys.add(paramKey);
    addedToInFlight = true;

    const response: AxiosResponse<DataKi> = yield call(
      api.get,
      '/data/ki',
      buildConfig(params)
    );
    const dataKi = response.data;

    yield put(
      DataKiActionCreators.fetchDataKiOnlyOneKiTagAverageSuccessAction(dataKi)
    );
  } catch (error: unknown) {
    yield put(DataKiActionCreators.renewDataKi());
    yield fork(handleErrorSaga, error);
  } finally {
    // ensure removal from in-flight set only if this saga added it
    if (addedToInFlight && paramKey) {
      inFlightOnlyOneTagKeys.delete(paramKey);
    }
  }
}

/**
 * 表示項目が変更されている場合並び替えに反映する
 * @param shuCode 種別コード
 */
export function* columnsOrderCheckSaga() {
  const fields: Column[] = yield select(dataKiDataColumnsSelector);

  const fieldCodes = fields.map((field) => field.code);

  // テーブル列の並び順
  const nowColumnsOrder: string[] | undefined = yield select(
    dataKiColumnsOrderSelector
  );

  // 並び替え未設定時はデフォルトを使用
  const columnsOrder =
    !nowColumnsOrder || nowColumnsOrder.length === 0
      ? fieldCodes
      : nowColumnsOrder;

  // ColumnsOrderとAPIとの差分の配列（SIS項目）
  const sisArray = fieldCodes.filter((item) => {
    return columnsOrder.indexOf(item) === -1 && ~item.indexOf('sis');
  });

  // ColumnsOrderとAPIとの差分の配列（差）
  const diffArray = fieldCodes.filter((item) => {
    return columnsOrder.indexOf(item) === -1 && ~item.indexOf('Diff');
  });

  // ColumnsOrderとAPIとの差分の配列（比率）
  const hirituArray = fieldCodes.filter((item) => {
    return columnsOrder.indexOf(item) === -1 && ~item.indexOf('Hiritu');
  });

  if (sisArray.length > 0) {
    sisArray.forEach((item) => {
      const target = columnsOrder.find((t) => {
        // 玉粗利のみ例外的にSP玉粗利がDK-SISの対象となるための分岐
        if (
          item.toLowerCase() === 'sistmaarari' &&
          !columnsOrder.includes('spTmaArari')
        ) {
          return false;
        }
        if (t.toLowerCase() === 'tmaarari') {
          return false;
        }
        if (
          t.toLowerCase() === 'sptmaarari' &&
          item.toLowerCase() === 'sistmaarari'
        ) {
          return true;
        }
        // ここまで
        return `sis${t.toLowerCase()}` === item.toLowerCase();
      });
      if (target) {
        const idx = columnsOrder.indexOf(target);
        return columnsOrder.splice(idx + 1, 0, item);
      }
      return target;
    });

    diffArray.forEach((item) => {
      const target = columnsOrder.find((t) => `${t}Diff` === item);
      if (target) {
        const idx = columnsOrder.indexOf(target);
        return columnsOrder.splice(idx + 1, 0, item);
      }
      return target;
    });

    hirituArray.forEach((item) => {
      const target = columnsOrder.find((t) => `${t}Hiritu` === item);
      if (target) {
        const idx = columnsOrder.indexOf(target);
        if (~columnsOrder.indexOf(`${target}Diff`)) {
          columnsOrder.splice(idx + 2, 0, item);
        }
        columnsOrder.splice(idx + 1, 0, item);
      }
    });
  }

  const columnsOrderWithNyushoS = insertMissingProperNyushoSColumnsOrder(
    columnsOrder,
    fieldCodes
  );

  const sorted = [...fieldCodes].sort((a, b) => {
    // 機種名は常に先頭
    if (b === fieldCodes.at(0)) {
      return 1;
    }

    return columnsOrderWithNyushoS.indexOf(b) >
      columnsOrderWithNyushoS.indexOf(a)
      ? -1
      : 1;
  });

  yield put(DataKiActionCreators.selectDataKiColumnsOrderAction(sorted));
}

/**
 * ドラッグ＆ドロップしたセルのIDを元に並び替え情報を登録する
 */
function* changeDataKiColumnsOrderSaga(action: ChangeDataKiColumnsOrderAction) {
  const tableData: DataKiData = yield select(dataKiOrderedDataSelector);

  const ordered = recalcColumnOrder(
    tableData.columns,
    action.payload.draggedId,
    action.payload.droppedId
  );

  yield put(DataKiActionCreators.selectDataKiColumnsOrderAction(ordered));
}

/**
 * リクエストした種別のデータとレスポンスの種別データが不一致だったときに、レスポンスの値で上書きする
 *
 * 種別の不一致は種別が削除されたとき、あるいは共有グループをまたいだお気に入りで発生する
 */
function* validateShuSaga() {
  const currentShu: ShuOption = yield select(
    modelReportsSelectedCurrentShuSelector
  );
  // currentShuが存在しない場合には初期リクエストになるため何もしない
  if (currentShu == null) {
    return;
  }

  const setting: DataKiParams = yield select(dataKiParamsSelector);

  const exist = existShuOption(setting, currentShu);
  if (exist) {
    return;
  }

  const settingsOptionsLoadingState: LoadingState = yield select(
    settingsOptionsKiLoadingSelector
  );

  if (settingsOptionsLoadingState !== 'loaded') {
    yield take(SettingsOptionsKiActionTypes.FETCH_SETTINGS_OPTIONS_KI_SUCCESS);
  }
  // FETCH_SETTINGS_OPTIONS_KI_SUCCESSを待つのでundefinedにならない
  const settingsOptions: SettingsOptionsKi = yield select(
    settingsOptionsKiSelector
  );

  const selectedShu: ShuOption[] = yield select(
    modelReportsSelectedShuSelector
  );

  const shuOption = convertShuOption(
    setting,
    settingsOptions.searchCondition.shuGroupList
  );

  if (shuOption == null) {
    // 変換先が存在しない種別になる場合何もしない
    // setting.shuGroupIdが0の場合に発生します
    return;
  }

  // currentShuの変更
  yield put(selectModelReportsCurrentShuAction(shuOption));

  // selectedShuの変更
  if (selectedShu.length > 0) {
    const validatedSelectedShu = removeDuplicateShuOptions([
      shuOption,
      ...selectedShu.filter((items) => items.code !== currentShu.code),
    ]);

    yield put(selectModelReportsShuAction(validatedSelectedShu));
  }
}

function* handleFetchDataKiSaga(api: Api) {
  yield takeEvery(DataKiActionTypes.FETCH_DATA_KI, fetchDataKiSaga, api);
  // 表示項目と並び替えのチェック
  yield takeEvery(
    DataKiActionTypes.FETCH_DATA_KI_SUCCESS,
    columnsOrderCheckSaga
  );

  yield takeEvery(DataKiActionTypes.FETCH_DATA_KI_SUCCESS, validateShuSaga);
}

function* handleSearchSaga() {
  // 並び替え時
  yield takeEvery(
    DataKiActionTypes.CHANGE_DATA_KI_COLUMNS_ORDER,
    changeDataKiColumnsOrderSaga
  );
}

function* handleFetchDataKiFilteredAverageSaga(api: Api) {
  yield takeLatest(
    DataKiActionTypes.FETCH_DATA_KI_FILTERED_AVERAGE_REQUEST,
    fetchDataKiFilteredAverageSaga,
    api
  );
}

function* handleFetchDataKiFilteredKiTagAverageSaga(api: Api) {
  yield takeEvery(
    DataKiActionTypes.FETCH_DATA_KI_FILTERED_KI_TAG_AVERAGE_REQUEST,
    fetchDataKiFilteredKiTagAverageSaga,
    api
  );
}

function* handleFetchDataKiOnlyOneKiTagAverageSaga(api: Api) {
  const inFlightOnlyOneTagKeys = new Set<string>();
  yield takeEvery(
    DataKiActionTypes.FETCH_DATA_KI_ONLY_ONE_KI_TAG_AVERAGE_REQUEST,
    fetchDataKiOnlyOneKiTagAverageSaga,
    api,
    inFlightOnlyOneTagKeys
  );
}

function* handleFetchDataKiExcludedKiTagAverageSaga(api: Api) {
  yield takeEvery(
    DataKiActionTypes.FETCH_DATA_KI_EXCLUDED_KI_TAG_AVERAGE_REQUEST,
    fetchDataKiExcludedKiTagAverageSaga,
    api
  );
}

export function* dataKiSagas(context: { api: Api }) {
  yield fork(handleFetchDataKiSaga, context.api);
  yield fork(handleSearchSaga);
  yield fork(handleFetchDataKiFilteredAverageSaga, context.api);
  yield fork(handleFetchDataKiFilteredKiTagAverageSaga, context.api);
  yield fork(handleFetchDataKiOnlyOneKiTagAverageSaga, context.api);
  yield fork(handleFetchDataKiExcludedKiTagAverageSaga, context.api);
}
